import React from 'react';
import { ui } from '../../config/uiSizes';
import CollapsibleFindingsSection from './CollapsibleFindingsSection';
import { isDivisionWarning, groupWarnings } from '../../utils/migrationWarnings';

interface MigrationWarningsBannerProps {
    /** Warning strings to surface (e.g. from job2Transform.warnings or job3Import.warnings). */
    warnings?: string[];
    /** Heading shown above the warning list. */
    title?: string;
    /** Stable id used to persist the section's open state. */
    sectionId?: string;
    /** Optional extra classes for the outer container. */
    className?: string;
}

/** Renders a single warning line, emphasising division-remapping warnings with an icon. */
const WarningItem: React.FC<{ warning: string; index: number }> = ({ warning, index }) => {
    const division = isDivisionWarning(warning);
    return (
        <li
            key={index}
            className={`flex items-start ${ui.gap.xs} ${ui.text.xs} ${division
                ? 'text-yellow-900 dark:text-yellow-100 font-medium'
                : 'text-yellow-700 dark:text-yellow-300'
                }`}
        >
            {division && (
                <svg
                    className={`${ui.svg.sm} text-amber-600 dark:text-amber-300 shrink-0 mt-0.5`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-label="Division remapping warning"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                </svg>
            )}
            <span className="break-words">{warning}</span>
        </li>
    );
};

/**
 * Yellow warning banner that surfaces transformation / import warnings in the
 * migration review views. Warnings are grouped by category (target-org readiness,
 * flow dependencies, division remapping, snapshot completeness, other) with a short
 * legend per group so a long list stays scannable. Division-remapping warnings are
 * further highlighted with an icon so users immediately notice when a `division_id`
 * remap was skipped and the resource was left unchanged.
 */
const MigrationWarningsBanner: React.FC<MigrationWarningsBannerProps> = ({
    warnings,
    title = 'Warnings',
    sectionId,
    className = '',
}) => {
    if (!warnings || warnings.length === 0) {
        return null;
    }

    const groups = groupWarnings(warnings);
    // Only show grouped sections (with legend headings) when there's more than one category —
    // a single-category list reads better flat.
    const grouped = groups.length > 1;

    return (
        <CollapsibleFindingsSection
            variant="yellow"
            title={title}
            iconPath="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            count={warnings.length}
            {...(sectionId ? { sectionId } : {})}
            className={className}
            summary={grouped ? `${groups.length} categories to review.` : undefined}
        >
            {grouped ? (
                <div className={`flex flex-col ${ui.gap.md}`}>
                    {groups.map((group) => (
                        <div key={group.category} className={`flex flex-col ${ui.gap.xs}`}>
                            <p className={`${ui.text.xs} font-semibold text-yellow-800 dark:text-yellow-200`}>
                                {group.label} ({group.items.length})
                            </p>
                            {group.description && (
                                <p className={`${ui.text.xxs} text-yellow-700 dark:text-yellow-300/80`}>
                                    {group.description}
                                </p>
                            )}
                            <ul className={`flex flex-col ${ui.gap.xs}`}>
                                {group.items.map((warning, index) => (
                                    <WarningItem key={index} warning={warning} index={index} />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            ) : (
                <ul className={`flex flex-col ${ui.gap.xs}`}>
                    {warnings.map((warning, index) => (
                        <WarningItem key={index} warning={warning} index={index} />
                    ))}
                </ul>
            )}
        </CollapsibleFindingsSection>
    );
};

export default MigrationWarningsBanner;

